<template>
  <div v-if="visible" ref="modalContainer" class="fixed inset-0 z-50 overflow-y-auto" @scroll="onModalScroll">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="visible"
        class="fixed inset-0 transition-opacity"
        @click="closeModal"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-6">
          <div>
          <h3 class="text-lg font-medium text-gray-900">
            {{ isEdit ? '编辑营销活动' : '创建营销活动' }}
          </h3>
            <p class="text-sm text-gray-500 mt-1 hidden sm:block">
              {{ isEdit ? '修改营销活动的配置信息和发送设置' : '创建邮件营销活动，批量发送邮件给目标客户' }}
            </p>
          </div>
          <button
            @click="closeModal"
            class="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- 步骤导航 -->
        <div class="mb-6">
          <div class="flex items-center justify-between">
            <div 
              v-for="(step, index) in steps" 
              :key="index"
              class="flex items-center"
              :class="{ 'flex-1': index < steps.length - 1 }"
            >
              <!-- 步骤圆圈 -->
              <div 
                class="flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium"
                :class="[
                  currentStep > index 
                    ? 'bg-blue-600 text-white' 
                    : currentStep === index 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-500'
                ]"
              >
                {{ index + 1 }}
              </div>
              
              <!-- 步骤标题 -->
              <div class="ml-2 flex-shrink-0">
                <p 
                  class="text-sm font-medium"
                  :class="[
                    currentStep >= index ? 'text-gray-900' : 'text-gray-500'
                  ]"
                >
                  {{ step.title }}
                </p>
              </div>
              
              <!-- 连接线 -->
              <div 
                v-if="index < steps.length - 1"
                class="flex-1 h-px mx-4"
                :class="[
                  currentStep > index ? 'bg-blue-600' : 'bg-gray-200'
                ]"
              ></div>
            </div>
          </div>
        </div>

        <!-- 表单内容 -->
        <form @submit.prevent="handleSubmit" class="space-y-6">
          <!-- 第一步：基本信息和模板 -->
          <div v-if="currentStep === 0" class="space-y-4">
            <!-- 活动名称和状态同一行 -->
            <div class="grid grid-cols-3 gap-4">
              <div class="col-span-2">
                <label for="name" class="block text-sm font-medium text-gray-700 mb-2">
                  活动名称 <span class="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  v-model="form.name"
                  type="text"
                  required
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                  placeholder="请输入活动名称"
                />
              </div>

              <div class="col-span-1">
                <label for="status" class="block text-sm font-medium text-gray-700 mb-2">
                  活动状态 <span class="text-red-500">*</span>
                </label>
                <select
                  id="status"
                  v-model="form.status"
                  required
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                >
                  <option value="draft">草稿</option>
                  <option value="scheduled">已计划</option>
                </select>
              </div>
            </div>

            <!-- 发送模板 -->
            <div>
              <label for="template" class="block text-sm font-medium text-gray-700 mb-2">
                发送模板 <span class="text-red-500">*</span>
              </label>
              <div class="flex items-center space-x-2">
                <SearchableSelect
                v-model="form.templateId"
                  :options="templateOptions"
                :disabled="loadingTemplates"
                  placeholder="请选择发送模板"
                  label-key="label"
                  value-key="value"
                  class="flex-1"
                  style="height: 40px;"
                />
                <button
                  v-if="form.templateId && !loadingTemplates"
                  @click="previewTemplate"
                  :disabled="previewingTemplate"
                  type="button"
                  class="flex-shrink-0 inline-flex items-center justify-center px-3 h-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="预览模板"
                >
                  <EyeIcon v-if="!previewingTemplate" class="h-4 w-4" />
                  <div v-else class="animate-spin h-4 w-4">
                    <svg class="w-4 h-4" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                </button>
              </div>
              <div v-if="loadingTemplates" class="mt-2 flex items-center text-sm text-gray-500">
                <svg class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                正在加载模板...
              </div>
            </div>

            <!-- 邮件主题 -->
            <div>
              <label for="subject" class="block text-sm font-medium text-gray-700 mb-2">
                邮件主题
              </label>
              <input
                id="subject"
                v-model="form.subject"
                type="text"
                class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                placeholder="自定义邮件主题，留空则使用模板默认主题"
              />
            </div>

            <div>
              <label for="description" class="block text-sm font-medium text-gray-700 mb-2">
                活动描述
              </label>
              <textarea
                id="description"
                v-model="form.description"
                rows="3"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                placeholder="请输入活动描述（可选）"
              ></textarea>
            </div>
          </div>

          <!-- 第二步：选择账户 -->
          <div v-if="currentStep === 1" class="space-y-6">
            <!-- 发件账户（多选） -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-3">
                发件账户 <span class="text-red-500">*</span>
              </label>
              
              <!-- 智能选择勾选框 -->
              <div class="mb-4">
                <label class="flex items-center">
                  <input
                    v-model="form.useSmartSelection"
                    type="checkbox"
                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                  />
                  <span class="ml-2 text-sm text-gray-700">智能选择发件账户</span>
                  <span class="ml-2 text-xs text-gray-500">(优先使用曾与客户有过邮件往来的账户)</span>
                </label>
              </div>
              
              <!-- 搜索和筛选框 -->
              <div class="mb-4 flex gap-3">
                <!-- 搜索框 -->
                <div class="flex-1">
                  <div class="relative">
                    <MagnifyingGlassIcon class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      v-model="accountFilters.search"
                      @input="onAccountSearchChange"
                      type="text"
                      placeholder="搜索账户名称或邮箱..."
                      class="w-full h-10 pl-10 pr-4 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                    />
                  </div>
                </div>
                
                <!-- 分组筛选 -->
                <div class="w-48">
                  <SearchableSelect
                    v-model="accountFilters.group"
                    :options="groupOptions"
                    placeholder="选择分组"
                    label-key="label"
                    value-key="value"
                    @update:model-value="onAccountFilterChange"
                    style="height: 40px;"
                  />
                </div>
              </div>
              
              <!-- 加载状态 -->
              <div v-if="loadingAccounts" class="text-center py-8 text-gray-500">
              <div class="animate-spin inline-block w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full"></div>
              <p class="mt-2 text-sm">加载发件账户中...</p>
            </div>
            
              <!-- 账户列表 - 固定高度可滚动 -->
              <div v-else class="border border-gray-200 rounded-lg overflow-hidden">
                <!-- 统计信息 -->
                <div class="px-4 py-3 bg-gray-50 flex items-center justify-between">
                  <div class="flex items-center gap-4">
                    <span class="text-sm text-gray-600">
                      共 {{ filteredEmailAccounts.length }} 个账户
                    </span>
                    <span v-if="form.fromAccountIds.length > 0" class="text-sm font-medium text-blue-600">
                      已选择 {{ form.fromAccountIds.length }} 个
                    </span>
                  </div>
                  <div class="flex gap-2">
                    <button
                      @click="selectAllAccounts"
                      type="button"
                      class="text-xs text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      全选
                    </button>
                    <button
                      @click="clearAllAccounts"
                      type="button"
                      class="text-xs text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      清空
                    </button>
                  </div>
                </div>
                
                <!-- 滚动容器 -->
                <div class="h-48 overflow-y-auto p-3">
                  <div v-if="filteredEmailAccounts.length === 0" class="text-center py-8 text-gray-500">
                    <div class="text-sm">{{ accountFilters.search || accountFilters.group ? '没有找到匹配的账户' : '暂无可用账户' }}</div>
                  </div>
                  
                  <!-- 横向卡片布局 -->
                  <div v-else class="flex flex-wrap gap-2">
                    <label
                      v-for="account in filteredEmailAccounts"
                  :key="account.id"
                      class="cursor-pointer"
                    >
                      <input 
                        type="checkbox" 
                        :value="account.id"
                        v-model="form.fromAccountIds"
                        class="sr-only" 
                      />
                      <span 
                        class="inline-flex items-center px-3 py-2 text-sm font-medium transition-colors border rounded-lg"
                        :class="form.fromAccountIds.includes(account.id) 
                          ? 'bg-blue-100 text-blue-800 border-blue-300' 
                          : 'bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200'"
                      >
                        <div class="text-left">
                          <div class="font-medium">{{ account.display_name || account.email.split('@')[0] }}</div>
                          <div class="text-xs opacity-75">{{ account.email }}</div>
                          <!-- 分组和提供商信息 -->
                          <div v-if="account.group || account.provider" class="mt-1 flex items-center gap-1">
                            <span
                              v-if="account.group"
                              class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
                              :style="{ backgroundColor: account.group.color + '15', color: account.group.color }"
                            >
                              {{ account.group.name }}
                            </span>
                            <span
                              v-if="account.provider"
                              class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600"
                            >
                              {{ account.provider }}
                            </span>
                </div>
              </div>
                      </span>
                    </label>
                  </div>
                </div>
            </div>
            </div>

          </div>
              
          <!-- 第三步：选择人群 - 全新简洁设计 -->
          <div v-if="currentStep === 2" class="space-y-5">
              <!-- 发送给所有客户选项 -->
            <div>
              <label class="flex items-start cursor-pointer">
                  <input
                    v-model="form.targetAllCustomers"
                    @change="onTargetAllCustomersChange"
                    type="checkbox"
                  class="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                />
                <div class="ml-3">
                  <div class="text-sm font-medium text-gray-900">发送给所有客户</div>
                  <div class="text-xs text-gray-600 mt-0.5">勾选后将发送给所有客户，忽略标签筛选条件</div>
                </div>
                </label>
              </div>

              <!-- 标签筛选区域 -->
              <div v-if="!form.targetAllCustomers">
              <!-- 搜索和筛选 -->
              <div class="flex gap-3 mb-4">
                  <div class="flex-1">
                    <div class="relative">
                      <MagnifyingGlassIcon class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        v-model="tagFilters.search"
                        @input="onTagSearchChange"
                        type="text"
                      placeholder="搜索标签..."
                      class="w-full h-10 pl-10 pr-4 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div class="w-48">
                    <SearchableSelect
                      v-model="tagFilters.group"
                      :options="tagGroupOptions"
                      placeholder="选择分组"
                      label-key="label"
                      value-key="value"
                      @update:model-value="onTagFilterChange"
                      style="height: 40px;"
                    />
                  </div>
                </div>
                
                <!-- 加载状态 -->
              <div v-if="loadingTags" class="text-center py-12">
                <div class="animate-spin inline-block w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full"></div>
                <p class="mt-3 text-sm text-gray-500">加载标签中...</p>
            </div>
            
              <!-- 标签列表 -->
              <div v-else>
                <!-- 标签摘要和操作说明 -->
                <div class="mb-4 p-3 bg-gray-50 rounded-lg">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-4 text-sm">
                      <span class="text-gray-600">
                        全部 {{ filteredCustomerTags.length }} 个标签
                      </span>
                      <span v-if="form.includeTags.length > 0" class="text-gray-600">
                      包含 {{ form.includeTags.length }} 个
                      </span>
                      <span v-if="form.excludeTags.length > 0" class="text-red-600 font-medium">
                      排除 {{ form.excludeTags.length }} 个
                      </span>
                    </div>
                      <button
                      v-if="form.includeTags.length > 0 || form.excludeTags.length > 0"
                        @click="clearAllTags"
                        type="button"
                      class="text-xs text-gray-500 hover:text-gray-700"
                      >
                      清空选择
                      </button>
                    </div>
                  </div>
                  
                <!-- 标签网格 - 固定高度可滚动 -->
                <div v-if="filteredCustomerTags.length === 0" class="text-center py-12 text-gray-500 border border-gray-200 rounded-lg">
                      <div class="text-sm">{{ tagFilters.search || tagFilters.group ? '没有找到匹配的标签' : '暂无可用标签' }}</div>
                    </div>
                    
                <div v-else class="border border-gray-200 rounded-lg p-3 max-h-64 overflow-y-auto">
                <div class="flex flex-wrap gap-2">
                          <label
                            v-for="tag in filteredCustomerTags"
                      :key="tag.id"
                            class="cursor-pointer"
                          >
                            <input 
                              type="checkbox" 
                              :value="tag.id"
                        :checked="form.includeTags.includes(tag.id) || form.excludeTags.includes(tag.id)"
                        @click="handleTagClick(tag.id, $event)"
                              class="sr-only" 
                            />
                            <span 
                        class="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors"
                        :class="getTagLabelClass(tag.id)"
                  >
                    {{ tag.name }}
                            </span>
                          </label>
                  </div>
              </div>

                <!-- 操作说明 - 简洁一行 -->
                <div class="mt-4 text-xs text-gray-500 flex items-center gap-4">
                  <span class="flex items-center gap-1.5">
                    <span class="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 border border-blue-300 rounded">标签</span>
                    单击包含
                            </span>
                  <span class="flex items-center gap-1.5">
                    <span class="px-2 py-0.5 text-xs bg-red-100 text-red-800 border border-red-300 rounded">标签</span>
                    双击排除
                    </span>
                  <span class="flex items-center gap-1.5">
                    <span class="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 border border-gray-300 rounded">标签</span>
                    再次单击取消
                  </span>
                  </div>
            </div>
          </div>

            <!-- 客户标签管理 -->
            <div>
              <button
                @click="showTagManagement = !showTagManagement"
                type="button"
                class="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <div class="flex items-center gap-2">
                  <span class="text-sm font-medium text-gray-900">客户标签管理（可选）</span>
                  <span v-if="form.addToExistingTags.length > 0 || form.newTags.length > 0" class="text-xs text-orange-600">
                    已配置
                    </span>
                </div>
                <ChevronDownIcon 
                  class="w-4 h-4 text-gray-400 transition-transform duration-200"
                  :class="{ 'rotate-180': showTagManagement }"
                />
              </button>
              
              <div v-show="showTagManagement" class="mt-3 space-y-3">
                <!-- 添加到现有标签 -->
                <div>
                  <div class="flex items-center justify-between mb-2">
                    <label class="text-sm font-medium text-gray-700">添加到现有标签</label>
                    <div class="relative">
                      <MagnifyingGlassIcon class="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                      <input
                        v-model="existingTagsSearch"
                        type="text"
                        placeholder="搜索..."
                        class="w-32 h-7 pl-6 pr-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                      />
                    </div>
                  </div>
                  <div class="border border-gray-200 rounded-lg p-3 max-h-32 overflow-y-auto">
                    <div v-if="filteredExistingTagsForManagement.length === 0" class="text-sm text-gray-500 text-center py-2">
                      {{ existingTagsSearch ? '未找到匹配的标签' : '暂无可用标签' }}
                    </div>
                    <div v-else class="flex flex-wrap gap-2">
                      <label
                        v-for="tag in filteredExistingTagsForManagement"
                        :key="`add-${tag.id}`"
                        class="cursor-pointer"
                      >
                        <input 
                          type="checkbox" 
                          :value="tag.id"
                          v-model="form.addToExistingTags"
                          class="sr-only" 
                        />
                        <span 
                          class="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors"
                          :class="form.addToExistingTags.includes(tag.id)
                            ? 'bg-orange-100 text-orange-800 border-orange-300' 
                            : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'"
                        >
                          {{ tag.name }}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
                
                <!-- 新建标签 -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">新建标签</label>
                    <div class="flex gap-2">
                        <input
                          v-model="newTagInput"
                          @keyup.enter="addNewTag"
                          type="text"
                          placeholder="输入新标签名称..."
                      class="flex-1 h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                        />
                      <button
                        @click="addNewTag"
                        :disabled="!newTagInput.trim()"
                        type="button"
                      class="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        添加
                      </button>
                    </div>
                    
                  <div v-if="form.newTags.length > 0" class="mt-2 flex flex-wrap gap-2">
                        <span 
                          v-for="(tagName, index) in form.newTags"
                          :key="`new-${index}`"
                          class="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-orange-100 text-orange-800 border border-orange-300 rounded-lg"
                        >
                          {{ tagName }}
                          <button
                            @click="removeNewTag(index)"
                            type="button"
                            class="ml-2 text-orange-600 hover:text-orange-800"
                          >
                        <XMarkIcon class="w-3 h-3" />
                          </button>
                        </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 第四步：发送配置 -->
          <div v-if="currentStep === 3" class="space-y-4">
            <h4 class="text-sm font-medium text-gray-900">发送设置</h4>
            
            <div>
              <label for="sendType" class="block text-sm font-medium text-gray-700 mb-2">
                发送类型 <span class="text-red-500">*</span>
              </label>
              <select
                id="sendType"
                v-model="form.sendType"
                @change="onSendTypeChange"
                required
                class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
              >
                <option value="immediate">立即发送</option>
                <option value="scheduled">定时发送</option>
              </select>
            </div>

            <div v-if="form.sendType === 'scheduled'">
              <label for="scheduledTime" class="block text-sm font-medium text-gray-700 mb-2">
                计划发送时间 <span class="text-red-500">*</span>
              </label>
              <input
                id="scheduledTime"
                v-model="form.scheduledTime"
                type="datetime-local"
                :required="form.sendType === 'scheduled'"
                class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
              />
            </div>

            <!-- 本次发送数量和筛选方式 -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label for="sendLimit" class="block text-sm font-medium text-gray-700 mb-2">
                  本次发送数量
                  <span class="text-xs text-gray-500 ml-1">(0 = 不限制)</span>
                </label>
                <input
                  id="sendLimit"
                  v-model.number="form.sendLimit"
                  type="number"
                  min="0"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                  placeholder="0 表示发送所有筛选出的客户"
                />
              </div>

              <div>
                <label for="sortMethod" class="block text-sm font-medium text-gray-700 mb-2">
                  筛选方式
                </label>
                <select
                  id="sortMethod"
                  v-model="form.sortMethod"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                >
                  <option value="last_email_desc">最后发送时间倒序（最近发送的优先）</option>
                  <option value="last_email_asc">最后发送时间升序（最久未发送的优先）</option>
                  <option value="created_desc">创建时间倒序（最新客户优先）</option>
                  <option value="created_asc">创建时间升序（最老客户优先）</option>
                </select>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <label for="dailyLimit" class="block text-sm font-medium text-gray-700 mb-2">
                  单个账号发送限制
                </label>
                <input
                  id="dailyLimit"
                  v-model.number="form.dailyLimit"
                  type="number"
                  min="1"
                  max="30000"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                  placeholder="500"
                />
              </div>

              <div>
                <label for="sendInterval" class="block text-sm font-medium text-gray-700 mb-2">
                  发送间隔（秒）
                </label>
                <input
                  id="sendInterval"
                  v-model.number="form.sendInterval"
                  type="number"
                  min="0"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                  placeholder="0.5"
                />
              </div>
            </div>

            <!-- 回复邮件模式设置 -->
            <div class="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div class="flex items-center">
                <input
                  id="reply_mode_enabled"
                  v-model="form.replyModeEnabled"
                  type="checkbox"
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label for="reply_mode_enabled" class="ml-2 block text-sm font-medium text-gray-900">
                  启用回复邮件模式
                  <span class="ml-1 text-xs text-blue-600 font-medium">(推荐)</span>
                </label>
              </div>
              
              <p class="text-xs text-gray-600">
                启用后，系统会查找收件箱中与客户的最新邮件，以回复的形式发送营销邮件，提高打开率和互动率
              </p>
              
              <!-- 回复模式详细设置 -->
              <div v-if="form.replyModeEnabled" class="space-y-3 ml-6 border-l-2 border-gray-300 pl-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    查找天数
                  </label>
                  <input
                    v-model.number="form.replySearchDays"
                    type="number"
                    min="1"
                    max="365"
                    class="w-full h-9 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500/20 focus:border-gray-500"
                    placeholder="90"
                  />
                  <p class="text-xs text-gray-500 mt-1">查找收件箱中多少天内的邮件进行回复（1-365天）</p>
                </div>
                
                <!-- 预览效果说明 -->
                <div class="bg-white p-3 rounded border border-gray-200">
                  <h4 class="text-sm font-medium text-gray-900 mb-2">回复模式效果：</h4>
                  <div class="space-y-1 text-xs text-gray-600">
                    <div class="flex items-center">
                      <CheckCircleIcon class="h-3 w-3 text-green-500 mr-1" />
                      <span>邮件以"Re: 原邮件主题"的形式发送</span>
                    </div>
                    <div class="flex items-center">
                      <CheckCircleIcon class="h-3 w-3 text-green-500 mr-1" />
                      <span>保持原有邮件线程，提高打开率</span>
                    </div>
                    <div class="flex items-center">
                      <CheckCircleIcon class="h-3 w-3 text-green-500 mr-1" />
                      <span>自动查找收件箱中最新的客户邮件</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 发送能力智能检测 -->
            <div v-if="sendCapacityCheck.show && sendCapacityCheck.type === 'error'" class="mt-4">
              <!-- 警告提示 -->
              <div class="bg-red-50 border border-red-200 rounded-lg p-4">
                <div class="flex items-start">
                  <svg class="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                  </svg>
                  <div class="flex-1">
                    <h5 class="text-sm font-semibold text-red-800 mb-2">⚠️ 发送能力不足</h5>
                    <p class="text-sm text-red-700 mb-3">
                      <template v-if="sendCapacityCheck.originalTargetCount > sendCapacityCheck.targetCount">
                        实际发送：<span class="font-bold">{{ sendCapacityCheck.targetCount }}</span> 位
                        <span class="text-xs">（已应用发送限制，原目标 {{ sendCapacityCheck.originalTargetCount }} 位）</span>
                      </template>
                      <template v-else>
                      目标客户：<span class="font-bold">{{ sendCapacityCheck.targetCount }}</span> 位 
                      </template>
                      | 当前发送能力：<span class="font-bold">{{ sendCapacityCheck.currentCapacity }}</span> 封
                    </p>
                    <div class="space-y-2 text-sm text-red-700">
                      <p class="font-medium">💡 必须优化以下配置才能继续：</p>
                      <ul class="list-disc list-inside space-y-1 ml-2">
                        <li v-if="sendCapacityCheck.suggestions.accountsNeeded">
                          <span class="font-bold">增加发件账户：</span>返回第2步，至少选择 <span class="font-bold text-red-800">{{ sendCapacityCheck.suggestions.accountsNeeded }}</span> 个邮件账户（当前已选 {{ sendCapacityCheck.selectedAccounts }} 个）
                        </li>
                        <li v-if="sendCapacityCheck.suggestions.increaseDailyLimit">
                          <span class="font-bold">提高单账户发送限制：</span>将限制提升至 <span class="font-bold text-red-800">{{ sendCapacityCheck.suggestions.suggestedDailyLimit }}</span> 封
                        </li>
                        <li v-if="sendCapacityCheck.suggestions.decreaseInterval">
                          <span class="font-bold">减小发送间隔：</span>将发送间隔调整为 <span class="font-bold text-red-800">{{ sendCapacityCheck.suggestions.suggestedInterval }}</span> 秒
                        </li>
                      </ul>
                      <p class="text-xs text-red-700 mt-3">
                        💡 建议：增加发送邮件账户或者提高单个账号发送限制和发送间隔时间
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 第五步：自动化跟进 -->
          <div v-if="currentStep === 4" class="space-y-4">
            <h4 class="text-sm font-medium text-gray-900">自动化跟进设置</h4>
            
            <!-- 启用自动化总开关 -->
            <div class="bg-gray-50 p-4 rounded-lg">
              <div class="flex items-center">
                <input
                  id="enableAutomation"
                  v-model="form.enableAutomation"
                  type="checkbox"
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label for="enableAutomation" class="ml-3 text-sm font-medium text-gray-900">
                  启用自动化跟进邮件
                </label>
              </div>
              <p class="mt-1 text-xs text-gray-500 ml-7">
                开启后，系统将根据客户的回复情况自动发送跟进邮件
              </p>
            </div>

            <div v-if="form.enableAutomation" class="space-y-6">
              <!-- 第二封邮件设置 -->
              <div class="border border-gray-200 rounded-lg p-4">
                <div class="flex items-center mb-3">
                  <input
                    id="secondEmailEnabled"
                    v-model="form.secondEmailEnabled"
                    type="checkbox"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label for="secondEmailEnabled" class="ml-3 text-sm font-medium text-gray-900">
                    第二封跟进邮件
                  </label>
                </div>

                <div v-if="form.secondEmailEnabled" class="space-y-4 ml-7">
                  <!-- 邮件模板选择 -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      邮件模板 <span class="text-red-500">*</span>
                    </label>
                    <SearchableSelect
                      v-model="form.secondEmailTemplate"
                      :options="templateOptions"
                      placeholder="选择邮件模板"
                      class="w-full"
                    />
                  </div>

                  <!-- 触发时间设置 -->
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">
                        未回复触发时间 (小时)
                      </label>
                      <input
                        v-model.number="form.secondEmailNoReplyHours"
                        type="number"
                        min="0"
                        max="168"
                        step="0.01"
                        class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                        placeholder="12"
                      />
                      <p class="mt-1 text-xs text-gray-500">设置为0表示不触发此条件</p>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">
                        已读未回复触发时间 (小时)
                      </label>
                      <input
                        v-model.number="form.secondEmailReadNoReplyHours"
                        type="number"
                        min="0"
                        max="168"
                        step="0.01"
                        class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                        placeholder="24"
                      />
                      <p class="mt-1 text-xs text-gray-500">设置为0表示不触发此条件</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 第三封邮件设置 -->
              <div class="border border-gray-200 rounded-lg p-4">
                <div class="flex items-center mb-3">
                  <input
                    id="thirdEmailEnabled"
                    v-model="form.thirdEmailEnabled"
                    type="checkbox"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label for="thirdEmailEnabled" class="ml-3 text-sm font-medium text-gray-900">
                    第三封跟进邮件
                  </label>
                </div>

                <div v-if="form.thirdEmailEnabled" class="space-y-4 ml-7">
                  <!-- 邮件模板选择 -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      邮件模板 <span class="text-red-500">*</span>
                    </label>
                    <SearchableSelect
                      v-model="form.thirdEmailTemplate"
                      :options="templateOptions"
                      placeholder="选择邮件模板"
                      class="w-full"
                    />
                  </div>

                  <!-- 触发时间设置 -->
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">
                        未回复触发时间 (小时)
                      </label>
                      <input
                        v-model.number="form.thirdEmailNoReplyHours"
                        type="number"
                        min="0"
                        max="168"
                        step="0.01"
                        class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                        placeholder="48"
                      />
                      <p class="mt-1 text-xs text-gray-500">设置为0表示不触发此条件</p>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-2">
                        已读未回复触发时间 (小时)
                      </label>
                      <input
                        v-model.number="form.thirdEmailReadNoReplyHours"
                        type="number"
                        min="0"
                        max="168"
                        step="0.01"
                        class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                        placeholder="96"
                      />
                      <p class="mt-1 text-xs text-gray-500">设置为0表示不触发此条件</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 预览自动化流程 -->
              <div v-if="form.secondEmailEnabled || form.thirdEmailEnabled" class="bg-blue-50 p-4 rounded-lg">
                <h5 class="text-sm font-medium text-blue-900 mb-2">自动化流程预览</h5>
                <div class="space-y-2 text-xs text-blue-800">
                  <div class="flex items-center">
                    <div class="w-2 h-2 bg-blue-600 rounded-full mr-2"></div>
                    <span>初次邮件发送</span>
                  </div>
                  <div v-if="form.secondEmailEnabled && form.secondEmailNoReplyHours > 0" class="flex items-center ml-4">
                    <div class="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <span>{{ form.secondEmailNoReplyHours }}小时后 → 第二封跟进 (未回复)</span>
                  </div>
                  <div v-if="form.secondEmailEnabled && form.secondEmailReadNoReplyHours > 0" class="flex items-center ml-4">
                    <div class="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <span>{{ form.secondEmailReadNoReplyHours }}小时后 → 第二封跟进 (已读未回复)</span>
                  </div>
                  <div v-if="form.thirdEmailEnabled && form.thirdEmailNoReplyHours > 0" class="flex items-center ml-4">
                    <div class="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                    <span>{{ form.thirdEmailNoReplyHours }}小时后 → 第三封跟进 (未回复)</span>
                  </div>
                  <div v-if="form.thirdEmailEnabled && form.thirdEmailReadNoReplyHours > 0" class="flex items-center ml-4">
                    <div class="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                    <span>{{ form.thirdEmailReadNoReplyHours }}小时后 → 第三封跟进 (已读未回复)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>

        <!-- 底部操作 -->
        <div class="mt-6 flex justify-between">
          <!-- 左侧：上一步按钮 -->
          <div>
            <button
              v-if="currentStep > 0"
              type="button"
              @click="previousStep"
              :disabled="loadingPreview || loading"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              上一步
            </button>
          </div>

          <!-- 右侧：取消和下一步/完成按钮 -->
          <div class="flex space-x-3">
            <button
              type="button"
              @click="closeModal"
              :disabled="loadingPreview || loading"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              取消
            </button>
            
            <!-- 最后一步显示完成按钮 -->
            <button
              v-if="currentStep === steps.length - 1"
              @click="handleSubmit"
              :disabled="loading"
              class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg v-if="loading" class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ loading ? '保存中...' : (isEdit ? '更新活动' : '创建活动') }}
            </button>

            <!-- 非最后一步显示下一步按钮 -->
            <div v-else class="flex flex-col items-end">
              <button
                type="button"
                @click="nextStep"
                :disabled="!canProceedToNextStep || loadingPreview"
                class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                <svg v-if="loadingPreview" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span v-if="loadingPreview && currentStep === 2">正在加载目标客户...</span>
                <span v-else>下一步</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- 模板预览模态框 -->
  <div v-if="showTemplatePreview" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity" @click="showTemplatePreview = false">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-900">模板预览</h3>
          <button @click="showTemplatePreview = false" class="text-gray-400 hover:text-gray-600">
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>
        <div v-if="templatePreviewData" class="border rounded-lg p-6">
          <div class="mb-4">
            <div class="text-sm text-gray-500">模板名称:</div>
            <div class="font-medium">{{ templatePreviewData.name }}</div>
          </div>
          <div class="mb-4">
            <div class="text-sm text-gray-500">邮件主题:</div>
            <div class="font-medium">{{ templatePreviewData.subject || '无邮件主题' }}</div>
          </div>
          <div class="border-t pt-4">
            <div class="text-sm text-gray-500 mb-2">邮件内容:</div>
            <div class="prose max-w-none" v-html="templatePreviewData.body_html || templatePreviewData.description || '暂无内容'"></div>
          </div>
        </div>
        <div v-else class="text-center py-8 text-gray-500">
          <div class="animate-spin inline-block w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full mb-2"></div>
          <p class="text-sm">加载模板内容中...</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, nextTick } from 'vue'
import { XMarkIcon, UsersIcon, ChevronLeftIcon, ChevronRightIcon, EyeIcon, MagnifyingGlassIcon, CheckIcon, ChevronDownIcon, CheckCircleIcon } from '@heroicons/vue/24/outline'
import emailTemplateService from '@/services/emailTemplateService'
import emailAccountService from '@/services/emailAccountService'
import customerService, { customerTagService } from '@/services/customerService'
import campaignService from '@/services/campaignService'
import { useToast } from 'vue-toastification'
import SearchableSelect from '@/components/common/SearchableSelect.vue'

// Props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  campaign: {
    type: Object,
    default: null
  }
})

// Emits
const emit = defineEmits(['close', 'submit'])

// 响应式数据
const loading = ref(false)
const loadingPreview = ref(false)
const previewData = ref(null)
const currentStep = ref(0)

// 步骤配置
const steps = ref([
  { title: '基本信息' },
  { title: '选择账户' },
  { title: '选择人群' },
  { title: '发送配置' },
  { title: '自动跟进' }
])

// 表单数据
const form = reactive({
  name: '',
  description: '',
  status: 'draft',
  subject: '',
  templateId: '',
  fromAccountIds: [],
  useSmartSelection: false,  // 智能选择发件账户
  sendType: 'immediate',
  scheduledTime: '',
  sendLimit: 0,  // 本次发送数量限制，0表示不限制
  sortMethod: 'last_email_asc',  // 筛选方式，默认最久未发送的优先
  dailyLimit: 500,
  sendInterval: 0.5,
  // 回复邮件模式设置
  replyModeEnabled: false,
  replySearchDays: 90,
  targetAllCustomers: false,
  includeTags: [],
  excludeTags: [],
  // 客户标签管理
  addToExistingTags: [],  // 要添加到现有标签的标签ID列表
  newTags: [],            // 要新建的标签名称列表
  
  // 自动化跟进设置
  enableAutomation: false,
  
  // 第二封邮件设置
  secondEmailEnabled: false,
  secondEmailTemplate: null,
  secondEmailNoReplyHours: 12,
  secondEmailReadNoReplyHours: 0,
  
  // 第三封邮件设置
  thirdEmailEnabled: false,
  thirdEmailTemplate: null,
  thirdEmailNoReplyHours: 24,
  thirdEmailReadNoReplyHours: 0
})

// 计算属性
const isEdit = computed(() => !!props.campaign)

// 模板选项 - 为SearchableSelect准备数据
const templateOptions = computed(() => {
  return templates.value.map(template => ({
    value: template.id,
    label: template.name,
    description: template.description || ''
  }))
})

// 真实数据
const templates = ref([])
const emailAccounts = ref([])
const customerTags = ref([])

// Toast通知
const toast = useToast()

// 数据加载状态
const loadingTemplates = ref(false)
const showTemplatePreview = ref(false)
const templatePreviewData = ref(null)
const previewingTemplate = ref(false)
const loadingAccounts = ref(false)
const loadingTags = ref(false)

// 账户筛选相关
const accountFilters = reactive({
  search: '',
  group: ''
})

let accountSearchTimeout = null

// 标签筛选相关
const tagFilters = reactive({
  search: '',
  group: ''
})

let tagSearchTimeout = null

// 新标签输入
const newTagInput = ref('')

// 标签管理展开状态
const showTagManagement = ref(false)

// 现有标签搜索
const existingTagsSearch = ref('')


// 模态框滚动容器ref
const modalContainer = ref(null)
let savedModalScrollPosition = 0

// 保存模态框滚动位置
const onModalScroll = () => {
  if (modalContainer.value) {
    savedModalScrollPosition = modalContainer.value.scrollTop
  }
}

// 恢复模态框滚动位置
const restoreModalScrollPosition = () => {
  if (modalContainer.value) {
    // 使用nextTick确保DOM已更新
    nextTick(() => {
      if (modalContainer.value) {
        modalContainer.value.scrollTop = savedModalScrollPosition
      }
    })
  }
}


// 计算属性
const filteredEmailAccounts = computed(() => {
  let filtered = [...emailAccounts.value]
  
  // 搜索过滤
  if (accountFilters.search.trim()) {
    const searchTerm = accountFilters.search.trim().toLowerCase()
    filtered = filtered.filter(account => {
      const displayName = (account.display_name || '').toLowerCase()
      const email = (account.email || '').toLowerCase()
      return displayName.includes(searchTerm) || email.includes(searchTerm)
    })
  }
  
  // 分组过滤
  if (accountFilters.group) {
    filtered = filtered.filter(account => {
      if (!account.group) return false
      // 支持按分组名称或ID筛选
      return account.group.name === accountFilters.group || account.group.id == accountFilters.group
    })
  }
  
  return filtered
})

const availableGroups = computed(() => {
  const groupsMap = new Map()
  emailAccounts.value.forEach(account => {
    if (account.group && account.group.name) {
      groupsMap.set(account.group.name, {
        name: account.group.name,
        id: account.group.id
      })
    }
  })
  return Array.from(groupsMap.values()).sort((a, b) => a.name.localeCompare(b.name))
})

// 为SearchableSelect准备的分组选项
const groupOptions = computed(() => {
  const options = [
    {
      label: '全部分组',
      value: '',
      id: 'all'
    }
  ]
  
  // 添加实际分组选项
  const groupItems = availableGroups.value.map(group => ({
    label: group.name,
    value: group.name,
    id: group.id
  }))
  
  return [...options, ...groupItems]
})

// 筛选后的标签
const filteredCustomerTags = computed(() => {
  let filtered = [...customerTags.value]
  
  // 搜索过滤
  if (tagFilters.search.trim()) {
    const searchTerm = tagFilters.search.trim().toLowerCase()
    filtered = filtered.filter(tag => {
      const tagName = (tag.name || '').toLowerCase()
      return tagName.includes(searchTerm)
    })
  }
  
  // 🔧 分组过滤 - 使用 group_info.name 进行过滤
  if (tagFilters.group) {
    filtered = filtered.filter(tag => {
      if (!tag.group_info) return false
      return tag.group_info.name === tagFilters.group
    })
  }
  
  return filtered
})


// 🔧 可用的标签分组 - 使用 group_info 提取分组信息
const availableTagGroups = computed(() => {
  const groupsMap = new Map()
  customerTags.value.forEach(tag => {
    if (tag.group_info) {
      // 使用 Map 以分组ID为key避免重复
      groupsMap.set(tag.group_info.id, tag.group_info)
    }
  })
  // 返回分组对象数组，按名称排序
  return Array.from(groupsMap.values()).sort((a, b) => a.name.localeCompare(b.name))
})

// 🔧 为标签分组SearchableSelect准备的选项 - 显示分组名称
const tagGroupOptions = computed(() => {
  const options = [
    {
      label: '全部分组',
      value: '',
      id: 'all'
    }
  ]
  
  // 添加实际分组选项 - 使用 name 作为 label 和 value
  const groupItems = availableTagGroups.value.map(group => ({
    label: group.name,
    value: group.name,
    id: group.id
  }))
  
  return [...options, ...groupItems]
})

// 筛选后的现有标签（用于"添加到现有标签"管理）
const filteredExistingTagsForManagement = computed(() => {
  let filtered = [...customerTags.value]
  
  // 搜索过滤
  if (existingTagsSearch.value.trim()) {
    const searchTerm = existingTagsSearch.value.trim().toLowerCase()
    filtered = filtered.filter(tag => {
      const tagName = (tag.name || '').toLowerCase()
      return tagName.includes(searchTerm)
    })
  }
  
  return filtered
})

// 方法
const previewTemplate = async () => {
  if (!form.templateId) {
    return
  }
  
  previewingTemplate.value = true
  try {
    // 获取模板详情
    const response = await emailTemplateService.getTemplate(form.templateId)
    
    if (response.success) {
      templatePreviewData.value = response.data
    } else {
      // 如果获取详情失败，尝试从options中获取基本信息
      const templateOption = templateOptions.value.find(t => t.value === form.templateId)
      if (templateOption) {
        templatePreviewData.value = {
          name: templateOption.label,
          subject: templateOption.description || '无邮件主题',
          body_html: '无法加载模板内容，请稍后重试'
        }
      }
      toast.error(response.message || '获取模板详情失败')
    }
  } catch (error) {
    console.error('预览模板失败:', error)
    toast.error('预览模板失败，请稍后重试')
    
    // 使用基本信息作为后备
    const templateOption = templateOptions.value.find(t => t.value === form.templateId)
    if (templateOption) {
      templatePreviewData.value = {
        name: templateOption.label,
        subject: templateOption.description || '无邮件主题',
        body_html: '无法加载模板内容，请稍后重试'
      }
    }
  } finally {
    previewingTemplate.value = false
  }
  
  // 显示预览模态框
  showTemplatePreview.value = true
}

const closeModal = () => {
  resetForm()
  currentStep.value = 0
  emit('close')
}

const resetForm = () => {
  Object.assign(form, {
    name: '',
    description: '',
    status: 'draft',
    subject: '',
    templateId: '',
    fromAccountIds: [],
    useSmartSelection: false,  // 智能选择发件账户
    sendType: 'immediate',
    scheduledTime: '',
    sendLimit: 0,  // 本次发送数量限制
    sortMethod: 'last_email_asc',  // 筛选方式
    dailyLimit: 500,
    sendInterval: 0.5,
    // 回复邮件模式设置
    replyModeEnabled: false,
    replySearchDays: 90,
    targetAllCustomers: false,
    includeTags: [],
    excludeTags: [],
    // 客户标签管理
    addToExistingTags: [],
    newTags: [],
    
    // 自动化跟进设置
    enableAutomation: false,
    
    // 第二封邮件设置
    secondEmailEnabled: false,
    secondEmailTemplate: null,
    secondEmailNoReplyHours: 12,
    secondEmailReadNoReplyHours: 0,
    
    // 第三封邮件设置
    thirdEmailEnabled: false,
    thirdEmailTemplate: null,
    thirdEmailNoReplyHours: 24,
    thirdEmailReadNoReplyHours: 0
  })
  
  // 重置其他状态
  previewData.value = null
  currentStep.value = 0
  newTagInput.value = ''
  showTagManagement.value = false
  
  // 重置筛选状态
  accountFilters.search = ''
  accountFilters.group = ''
  tagFilters.search = ''
  tagFilters.group = ''
  existingTagsSearch.value = ''
}

const onSendTypeChange = () => {
  if (form.sendType === 'immediate') {
    form.scheduledTime = ''
  }
}

// 处理发送给所有客户的变化
const onTargetAllCustomersChange = () => {
  if (form.targetAllCustomers) {
    // 如果勾选发送给所有客户，清空标签选择
    form.includeTags = []
    form.excludeTags = []
  }
}

const previewCustomers = async (showToast = false, autoFillSendLimit = false) => {
  loadingPreview.value = true
  
  try {
    const response = await campaignService.previewCustomers({
      target_all_customers: form.targetAllCustomers,
      include_tag_ids: form.includeTags,
      exclude_tag_ids: form.excludeTags
    })
    
    previewData.value = response
    
    const count = response.total_count || response.totalCount || 0
    
    // 自动填充发送数量
    if (autoFillSendLimit && count > 0) {
      form.sendLimit = count
    }
    
    // 如果需要显示提示（且客户数量大于0）
    if (showToast && count > 0) {
      toast.success(`已匹配到 ${count} 位目标客户`)
    }
  } catch (error) {
    console.error('获取目标客户数量失败:', error)
    previewData.value = null
    if (showToast) {
      toast.error('获取目标客户失败，请重试')
    }
  } finally {
    loadingPreview.value = false
  }
}

// 步骤控制
const canProceedToNextStep = computed(() => {
  switch (currentStep.value) {
    case 0: // 基本信息和模板
      return form.name && form.status && form.templateId
    case 1: // 选择账户
      return form.fromAccountIds.length > 0
    case 2: // 选择人群
      return form.targetAllCustomers || form.includeTags.length > 0 || form.excludeTags.length > 0
    case 3: // 发送设置
      // 基础验证
      const basicValid = form.sendType && (form.sendType !== 'scheduled' || form.scheduledTime)
      if (!basicValid) return false
      
      // 检查发送能力是否充足（只有error类型才阻止，warning可以继续）
      if (sendCapacityCheck.value.show && sendCapacityCheck.value.type === 'error') {
        return false
      }
      
      return true
    default:
      return false
  }
})

// 发送能力智能检测
const sendCapacityCheck = computed(() => {
  // 只在步骤3（发送配置）且有预览数据时显示
  if (currentStep.value !== 3 || !previewData.value) {
    return { show: false }
  }

  const targetCount = previewData.value.total_count || previewData.value.totalCount || 0
  const selectedAccounts = form.fromAccountIds.length
  // 确保dailyLimit至少为1，避免0或负数
  const dailyLimit = Math.max(1, form.dailyLimit || 500)
  
  // 计算实际会发送的客户数（考虑本次发送数量限制）
  // 如果设置了 sendLimit 且大于 0，则取 min(targetCount, sendLimit)
  const actualSendCount = (form.sendLimit && form.sendLimit > 0) 
    ? Math.min(targetCount, form.sendLimit) 
    : targetCount
  
  // 如果没有目标客户，不显示
  if (actualSendCount === 0) {
    return { show: false }
  }

  // 如果没有选择账户，显示错误
  if (selectedAccounts === 0) {
    return {
      show: true,
      type: 'error',
      targetCount: actualSendCount,
      originalTargetCount: targetCount,
      selectedAccounts: 0,
      currentCapacity: 0,
      suggestions: {
        accountsNeeded: Math.ceil(actualSendCount / dailyLimit)
      }
    }
  }

  // 计算当前每日发送能力：账户数 × 单账户日发送限制
  const currentCapacity = selectedAccounts * dailyLimit
  
  // 计算需要的天数
  const daysNeeded = currentCapacity > 0 ? Math.ceil(actualSendCount / currentCapacity) : Infinity
  
  // 计算建议
  const suggestions = {}
  
  // 建议需要的账户数（假设希望1天内完成）
  const accountsNeeded = Math.ceil(actualSendCount / dailyLimit)
  if (accountsNeeded > selectedAccounts) {
    suggestions.accountsNeeded = accountsNeeded
  }
  
  // 建议的单账户日发送限制（如果只有1个账户）
  if (selectedAccounts === 1 && actualSendCount > dailyLimit) {
    suggestions.suggestedDailyLimit = Math.min(Math.ceil(actualSendCount), 30000)
    suggestions.increaseDailyLimit = true
  }
  
  // 建议的发送间隔（假设想在1天内完成，1天=86400秒）
  if (currentCapacity < actualSendCount && selectedAccounts > 0) {
    const totalSecondsInDay = 86400
    const optimalInterval = Math.max(0.1, totalSecondsInDay / (actualSendCount / selectedAccounts))
    if (optimalInterval < (form.sendInterval || 0.5)) {
      suggestions.suggestedInterval = Math.round(optimalInterval * 10) / 10
      suggestions.decreaseInterval = true
    }
  }
  
  // 判断类型（严格判断：只要能力不足就阻止）
  let type = 'success'
  
  // 只要当前能力小于实际发送数量，就认为不足，阻止进入下一步
  if (currentCapacity < actualSendCount) {
    type = 'error'
  }
  
  return {
    show: true,
    type,
    targetCount: actualSendCount,
    originalTargetCount: targetCount,
    selectedAccounts,
    currentCapacity,
    daysNeeded: daysNeeded === Infinity ? '∞' : daysNeeded,
    suggestions
  }
})

const nextStep = async () => {
  if (canProceedToNextStep.value && currentStep.value < steps.value.length - 1) {
    // 如果从步骤2（选择人群）进入步骤3（发送配置），自动预览目标客户（用于发送能力检测）
    if (currentStep.value === 2) {
      await previewCustomers(true, true) // 显示toast提示 + 自动填充发送数量
      
      // 检查客户数量，如果为0则不跳转
      const count = previewData.value?.total_count || previewData.value?.totalCount || 0
      if (count === 0) {
        toast.warning('当前筛选条件下没有匹配到任何客户，请调整筛选条件')
        return // 阻止跳转到下一步
      }
    }
    currentStep.value++
  }
}

const previousStep = () => {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}

// 发件账户选择
const toggleAccount = (accountId) => {
  const index = form.fromAccountIds.indexOf(accountId)
  if (index > -1) {
    // 如果已选中，则移除
    form.fromAccountIds.splice(index, 1)
  } else {
    // 如果未选中，则添加
    form.fromAccountIds.push(accountId)
  }
}

// 账户筛选方法
const onAccountSearchChange = () => {
  // 防抖搜索
  clearTimeout(accountSearchTimeout)
  accountSearchTimeout = setTimeout(() => {
    // 搜索逻辑已在计算属性中处理，这里可以添加额外逻辑如埋点等
  }, 300)
}

const onAccountFilterChange = () => {
  // 分组筛选变化
  // 筛选逻辑已在计算属性中处理
}

const selectAllAccounts = () => {
  // 全选当前筛选结果中的账户
  const currentAccountIds = filteredEmailAccounts.value.map(account => account.id)
  const newIds = new Set([...form.fromAccountIds, ...currentAccountIds])
  form.fromAccountIds = Array.from(newIds)
}

const clearAllAccounts = () => {
  // 清空选择
  form.fromAccountIds = []
}

// 标签筛选方法
const onTagSearchChange = () => {
  // 防抖搜索
  clearTimeout(tagSearchTimeout)
  tagSearchTimeout = setTimeout(() => {
    // 搜索逻辑已在计算属性中处理
  }, 300)
}


const onTagFilterChange = () => {
  // 分组筛选变化
  // 筛选逻辑已在计算属性中处理
}

const selectAllTags = () => {
  // 全选当前筛选结果中的标签到包含标签
  const currentTagIds = filteredCustomerTags.value.map(tag => tag.id)
  const newIds = new Set([...form.includeTags, ...currentTagIds])
  form.includeTags = Array.from(newIds)
  // 从排除标签中移除这些标签
  form.excludeTags = form.excludeTags.filter(tagId => !newIds.has(tagId))
}

const clearAllTags = () => {
  // 清空所有标签选择
  form.includeTags = []
  form.excludeTags = []
}

// 标签选择 - 新的交互逻辑：单击包含，双击排除，再次单击取消
let lastClickTime = 0
let lastClickId = null

const handleTagClick = (tagId, event) => {
  event.preventDefault()
  
  const now = Date.now()
  const timeDiff = now - lastClickTime
  const isDoubleClick = timeDiff < 300 && lastClickId === tagId
  
  lastClickTime = now
  lastClickId = tagId
  
  const isIncluded = form.includeTags.includes(tagId)
  const isExcluded = form.excludeTags.includes(tagId)
  
  if (isDoubleClick) {
    // 双击：设置为排除
    if (!isExcluded) {
      // 从包含中移除（如果有）
      const includeIndex = form.includeTags.indexOf(tagId)
      if (includeIndex > -1) {
        form.includeTags.splice(includeIndex, 1)
      }
      // 添加到排除
      form.excludeTags.push(tagId)
    }
  } else {
    // 单击：循环切换状态
    if (isIncluded) {
      // 已包含 -> 取消选择
      const index = form.includeTags.indexOf(tagId)
      form.includeTags.splice(index, 1)
    } else if (isExcluded) {
      // 已排除 -> 取消选择
      const index = form.excludeTags.indexOf(tagId)
      form.excludeTags.splice(index, 1)
    } else {
      // 未选择 -> 包含
      form.includeTags.push(tagId)
    }
  }
}

// 获取标签的样式类（与"添加到现有标签"样式一致）
const getTagLabelClass = (tagId) => {
  const isIncluded = form.includeTags.includes(tagId)
  const isExcluded = form.excludeTags.includes(tagId)
  
  if (isIncluded) {
    return 'bg-blue-100 text-blue-800 border-blue-300'
  } else if (isExcluded) {
    return 'bg-red-100 text-red-800 border-red-300'
  } else {
    return 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
  }
}

// 旧的标签选择方法（保留以防其他地方使用）
const toggleIncludeTag = (tagId) => {
  const index = form.includeTags.indexOf(tagId)
  if (index > -1) {
    form.includeTags.splice(index, 1)
  } else {
    form.includeTags.push(tagId)
    const excludeIndex = form.excludeTags.indexOf(tagId)
    if (excludeIndex > -1) {
      form.excludeTags.splice(excludeIndex, 1)
    }
  }
}

const toggleExcludeTag = (tagId) => {
  const index = form.excludeTags.indexOf(tagId)
  if (index > -1) {
    form.excludeTags.splice(index, 1)
  } else {
    form.excludeTags.push(tagId)
    const includeIndex = form.includeTags.indexOf(tagId)
    if (includeIndex > -1) {
      form.includeTags.splice(includeIndex, 1)
    }
  }
}

// 新标签管理
const addNewTag = () => {
  const tagName = newTagInput.value.trim()
  if (tagName && !form.newTags.includes(tagName)) {
    // 检查是否与现有标签名称重复
    const existingTag = customerTags.value.find(tag => 
      tag.name.toLowerCase() === tagName.toLowerCase()
    )
    
    if (existingTag) {
      toast.error('标签名称已存在，请使用不同的名称')
      return
    }
    
    form.newTags.push(tagName)
    newTagInput.value = ''
  }
}

const removeNewTag = (index) => {
  form.newTags.splice(index, 1)
}

// 处理服务返回的错误信息
const handleServiceError = (result) => {
  console.error('操作失败:', result)
  
  // 处理验证错误（字段级错误）
  if (result.details && typeof result.details === 'object') {
    const errors = result.details
    let errorMessage = '表单验证失败：\n'
    
    // 遍历错误字段
    for (const [field, messages] of Object.entries(errors)) {
      if (Array.isArray(messages)) {
        // 字段映射：将API字段名映射为用户友好的中文名称
        const fieldMap = {
          'second_email_template': '第二封邮件模板',
          'third_email_template': '第三封邮件模板',
          'second_email_no_reply_hours': '第二封邮件未回复触发时间',
          'second_email_read_no_reply_hours': '第二封邮件已读未回复触发时间',
          'third_email_no_reply_hours': '第三封邮件未回复触发时间',
          'third_email_read_no_reply_hours': '第三封邮件已读未回复触发时间',
          'template_id': '邮件模板',
          'name': '活动名称',
          'from_account_ids': '发件账户'
        }
        
        const fieldName = fieldMap[field] || field
        errorMessage += `• ${fieldName}: ${messages.join('，')}\n`
      }
    }
    
    toast.error(errorMessage.trim())
  } else {
    // 处理一般错误
    const message = result.error || result.message || '操作失败'
    toast.error(message)
  }
}

const handleSubmit = async () => {
  loading.value = true
  
  try {
    const campaignData = {
      name: form.name,
      description: form.description,
      status: form.status,
      subject: form.subject,
      template_id: form.templateId,
      from_account_ids: form.fromAccountIds,
      use_smart_selection: form.useSmartSelection,  // 智能选择发件账户
      send_type: form.sendType,
      scheduled_time: form.scheduledTime || null,
      send_limit: form.sendLimit || 0,  // 本次发送数量限制
      sort_method: form.sortMethod || 'last_email_asc',  // 筛选方式
      daily_limit: form.dailyLimit,
      send_interval_seconds: form.sendInterval,
      // 回复邮件模式字段
      reply_mode_enabled: form.replyModeEnabled,
      reply_search_days: form.replySearchDays,
      target_all_customers: form.targetAllCustomers,
      include_tag_ids: form.includeTags,
      exclude_tag_ids: form.excludeTags,
      // 客户标签管理
      add_to_existing_tags: form.addToExistingTags,  // 添加到现有标签
      new_tags: form.newTags,  // 新建标签
      // 自动化字段
      enable_automation: form.enableAutomation,
      second_email_enabled: form.secondEmailEnabled,
      second_email_template: form.secondEmailTemplate,
      second_email_no_reply_hours: form.secondEmailNoReplyHours,
      second_email_read_no_reply_hours: form.secondEmailReadNoReplyHours,
      third_email_enabled: form.thirdEmailEnabled,
      third_email_template: form.thirdEmailTemplate,
      third_email_no_reply_hours: form.thirdEmailNoReplyHours,
      third_email_read_no_reply_hours: form.thirdEmailReadNoReplyHours
    }
    
    let result
    if (isEdit.value) {
      result = await campaignService.updateCampaign(props.campaign.id, campaignData)
      if (result.success) {
        toast.success('营销活动更新成功')
        emit('submit', result)
        closeModal()
      } else {
        // 处理更新失败的情况
        handleServiceError(result)
      }
    } else {
      result = await campaignService.createCampaign(campaignData)
      if (result.success) {
        toast.success('营销活动创建成功')
        emit('submit', result)
        closeModal()
      } else {
        // 处理创建失败的情况
        handleServiceError(result)
      }
    }
  } catch (error) {
    console.error('提交失败:', error)
    const message = error.response?.data?.detail || error.message || '操作失败'
    toast.error(message)
  } finally {
    loading.value = false
  }
}

// 初始化表单
const initForm = () => {
  if (props.campaign) {
    // 编辑模式：使用现有数据初始化
    // 从嵌套对象中提取ID
    const templateId = props.campaign.template?.id || props.campaign.template_id || props.campaign.templateId || ''
    const fromAccountIds = props.campaign.from_accounts 
      ? props.campaign.from_accounts.map(acc => acc.id)
      : (props.campaign.from_account_ids || props.campaign.fromAccountIds || [])
    const includeTags = props.campaign.include_tags 
      ? props.campaign.include_tags.map(tag => tag.id)
      : (props.campaign.include_tag_ids || props.campaign.includeTags || [])
    const excludeTags = props.campaign.exclude_tags 
      ? props.campaign.exclude_tags.map(tag => tag.id)
      : (props.campaign.exclude_tag_ids || props.campaign.excludeTags || [])
    
    // 提取自动化模板ID（可能是对象或ID）
    const secondEmailTemplate = typeof props.campaign.second_email_template === 'object' && props.campaign.second_email_template?.id
      ? props.campaign.second_email_template.id
      : props.campaign.second_email_template
    const thirdEmailTemplate = typeof props.campaign.third_email_template === 'object' && props.campaign.third_email_template?.id
      ? props.campaign.third_email_template.id
      : props.campaign.third_email_template
    
    Object.assign(form, {
      name: props.campaign.name || '',
      description: props.campaign.description || '',
      status: props.campaign.status || 'draft',
      subject: props.campaign.subject || '',
      templateId: templateId,
      fromAccountIds: fromAccountIds,
      useSmartSelection: props.campaign.use_smart_selection || props.campaign.useSmartSelection || false,
      sendType: props.campaign.send_type || props.campaign.sendType || 'immediate',
      scheduledTime: props.campaign.scheduled_time || props.campaign.scheduledTime || '',
      sendLimit: props.campaign.send_limit || props.campaign.sendLimit || 0,
      sortMethod: props.campaign.sort_method || props.campaign.sortMethod || 'last_email_asc',
      dailyLimit: props.campaign.daily_limit || props.campaign.dailyLimit || 500,
      sendInterval: props.campaign.send_interval_seconds || props.campaign.sendInterval || 0.5,
      // 回复邮件模式字段
      replyModeEnabled: props.campaign.reply_mode_enabled ?? props.campaign.replyModeEnabled ?? false,
      replySearchDays: props.campaign.reply_search_days ?? props.campaign.replySearchDays ?? 90,
      targetAllCustomers: props.campaign.target_all_customers ?? props.campaign.targetAllCustomers ?? false,
      includeTags: includeTags,
      excludeTags: excludeTags,
      addToExistingTags: props.campaign.add_to_existing_tags || props.campaign.addToExistingTags || [],
      newTags: props.campaign.new_tags || props.campaign.newTags || [],
      // 自动化字段
      enableAutomation: props.campaign.enable_automation ?? props.campaign.enableAutomation ?? false,
      secondEmailEnabled: props.campaign.second_email_enabled ?? props.campaign.secondEmailEnabled ?? false,
      secondEmailTemplate: secondEmailTemplate || null,
      secondEmailNoReplyHours: props.campaign.second_email_no_reply_hours ?? props.campaign.secondEmailNoReplyHours ?? 24,
      secondEmailReadNoReplyHours: props.campaign.second_email_read_no_reply_hours ?? props.campaign.secondEmailReadNoReplyHours ?? 48,
      thirdEmailEnabled: props.campaign.third_email_enabled ?? props.campaign.thirdEmailEnabled ?? false,
      thirdEmailTemplate: thirdEmailTemplate || null,
      thirdEmailNoReplyHours: props.campaign.third_email_no_reply_hours ?? props.campaign.thirdEmailNoReplyHours ?? 72,
      thirdEmailReadNoReplyHours: props.campaign.third_email_read_no_reply_hours ?? props.campaign.thirdEmailReadNoReplyHours ?? 96
    })
  } else {
    // 新建模式：使用默认值
    Object.assign(form, {
      name: '',
      description: '',
      status: 'draft',
      subject: '',
      templateId: '',
      fromAccountIds: [],
      useSmartSelection: false,
      sendType: 'immediate',
      scheduledTime: '',
      sendLimit: 0,  // 本次发送数量限制
      sortMethod: 'last_email_asc',  // 筛选方式
      dailyLimit: 500,
      sendInterval: 0.5,
      targetAllCustomers: false,
      includeTags: [],
      excludeTags: [],
      addToExistingTags: [],
      newTags: [],
      
      // 自动化跟进设置
      enableAutomation: false,
      
      // 第二封邮件设置
      secondEmailEnabled: false,
      secondEmailTemplate: null,
      secondEmailNoReplyHours: 12,
      secondEmailReadNoReplyHours: 0,
      
      // 第三封邮件设置
      thirdEmailEnabled: false,
      thirdEmailTemplate: null,
      thirdEmailNoReplyHours: 24,
      thirdEmailReadNoReplyHours: 0
    })
  }
  
  // 重置其他状态
  previewData.value = null
  currentStep.value = 0
  newTagInput.value = ''
  showTagManagement.value = false
  
  // 重置筛选状态
  accountFilters.search = ''
  accountFilters.group = ''
  tagFilters.search = ''
  tagFilters.group = ''
  existingTagsSearch.value = ''
}

// 监听器
watch(() => props.visible, (newVal) => {
  if (newVal) {
    initForm()
  }
})

// 获取最小日期时间（当前时间+1小时）
const getMinDateTime = () => {
  const now = new Date()
  now.setHours(now.getHours() + 1)
  return now.toISOString().slice(0, 16)
}

// 加载数据的方法
const loadTemplates = async () => {
  loadingTemplates.value = true
  try {
    const response = await emailTemplateService.getTemplates()
    templates.value = response.results || response
  } catch (error) {
    console.error('加载邮件模板失败:', error)
    toast.error('加载邮件模板失败')
  } finally {
    loadingTemplates.value = false
  }
}

const loadEmailAccounts = async () => {
  loadingAccounts.value = true
  try {
    // 🔧 设置page_size为9999，确保加载所有账户
    const response = await emailAccountService.getEmailAccounts({ page_size: 9999 })
    emailAccounts.value = response.results || response
  } catch (error) {
    console.error('加载发件账户失败:', error)
    toast.error('加载发件账户失败')
  } finally {
    loadingAccounts.value = false
  }
}

const loadCustomerTags = async () => {
  loadingTags.value = true
  try {
    const response = await customerService.customerTagService.getTags()
    if (response.success) {
      customerTags.value = response.data.results || response.data
    } else {
      throw new Error(response.message)
    }
  } catch (error) {
    console.error('加载客户标签失败:', error)
    toast.error('加载客户标签失败')
  } finally {
    loadingTags.value = false
  }
}

// 初始化数据
const initializeData = async () => {
  await Promise.all([
    loadTemplates(),
    loadEmailAccounts(), 
    loadCustomerTags()
  ])
}

onMounted(() => {
  // 设置最小计划时间
  const scheduledTimeInput = document.getElementById('scheduledTime')
  if (scheduledTimeInput) {
    scheduledTimeInput.min = getMinDateTime()
  }
  
  // 加载数据
  initializeData()
})
</script>

<style scoped>
/* 预览模态框样式 */
.prose {
  max-width: none;
}

.prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
  margin-top: 0.5em;
  margin-bottom: 0.5em;
}

.prose p {
  margin-top: 0.5em;
  margin-bottom: 0.5em;
}

/* 确保预览内容正确显示 */
.prose img {
  max-width: 100%;
  height: auto;
}

/* 旋转动画 */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>